/**
 * useWebSocket - Real-time WebSocket Hook
 *
 * Manages WebSocket connection for real-time notification delivery.
 * When WebSocket messages are received, it invalidates TanStack Query
 * to trigger immediate data refresh instead of waiting for polling.
 *
 * Features:
 * - Auto-connect when authenticated AND in an organization
 * - Query invalidation on notification messages
 * - Optional toast notifications for urgent items
 * - Connection state tracking
 * - Graceful degradation (falls back to polling if WS unavailable)
 */
import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { getIdToken } from '../lib/api';
import { getWebSocketClient, resetWebSocketClient, BroadcastMessage } from '../lib/websocket/WebSocketClient';
import { useToast, type ToastActionConfig } from '@/components/ui/use-toast';
import { NotificationType, WS_ENTITY_TYPES, isHiddenInAppType, parseEntityPath } from '@ruta/shared';
import { resolveEntityRoute } from '../lib/entityRoutes';
import { setMessageDraft } from './useMessageDraft';
import { playAlertCueOnce, playCashRegister, playNewCustomerChime } from '../lib/notificationSounds';
import { queryKeys } from '../lib/queryKeys';
import { invalidateDispatchedVisits } from '../lib/dispatchedVisits';
import type { QueryClient } from '@tanstack/react-query';
import type { UserProfileResponse } from '../lib/api/routes/users';

function getReplyAction(type: string | undefined, actionLink: string | undefined) {
  if (type !== NotificationType.CUSTOMER_MESSAGE || !actionLink) return null;
  const ref = parseEntityPath(actionLink);
  if (ref?.type !== 'CUSTOMER') return null;
  const base = resolveEntityRoute(ref);
  if (!base) return null;
  const route = `${base}${base.includes('?') ? '&' : '?'}tab=messages`;
  return { route, customerId: ref.params.id };
}

function getSetRateAction(type: string | undefined, actionLink: string | undefined) {
  if (type !== NotificationType.SUBSCRIPTION_REQUESTED || !actionLink) return null;
  const ref = parseEntityPath(actionLink);
  if (ref?.type !== 'SUBSCRIPTION') return null;
  const route = resolveEntityRoute(ref);
  if (!route) return null;
  return { route };
}

const PAYMENT_ALERT_TYPES = new Set<string>([
  NotificationType.PAYMENT_FAILED,
  NotificationType.PAYMENT_OVERDUE,
  NotificationType.PAYMENT_RETRY,
]);

/**
 * Alerts that toast on arrival regardless of priority, because they're the ones
 * the office wants to catch mid-task (RUT-3168). Everything else still follows
 * the priority gate below.
 */
const ALWAYS_TOAST_TYPES = new Set<string>([
  NotificationType.VISIT_PAYMENT_RECEIVED,
  NotificationType.BATCH_CHARGE_COMPLETED,
  NotificationType.CUSTOMER_PORTAL_REGISTERED,
]);

/**
 * Deep-link button for the always-toast alerts, so "$120.00 from Maria Lopez"
 * is one click from the visit that earned it. The nightly batch summary has no
 * single entity to open, so it toasts without a button.
 */
export function getMoneyAlertAction(type: string | undefined, actionLink: string | undefined) {
  if (!actionLink) return null;
  const label =
    type === NotificationType.VISIT_PAYMENT_RECEIVED
      ? 'View visit'
      : type === NotificationType.CUSTOMER_PORTAL_REGISTERED
        ? 'View customer'
        : null;
  if (!label) return null;
  const ref = parseEntityPath(actionLink);
  const route = ref ? resolveEntityRoute(ref) : null;
  if (!route) return null;
  return { route, label };
}

/**
 * The sound each alert gets. Money — a single visit or the whole night's batch —
 * rings the register; a new customer gets its own welcoming chime so the two are
 * distinguishable without looking at the screen.
 */
export function getNotificationSound(type: string | undefined): (() => void) | null {
  switch (type) {
    case NotificationType.VISIT_PAYMENT_RECEIVED:
    case NotificationType.BATCH_CHARGE_COMPLETED:
      return playCashRegister;
    case NotificationType.CUSTOMER_PORTAL_REGISTERED:
      return playNewCustomerChime;
    default:
      return null;
  }
}

export function getViewCustomerAction(type: string | undefined, actionLink: string | undefined) {
  if (!type || !PAYMENT_ALERT_TYPES.has(type) || !actionLink) return null;
  const ref = parseEntityPath(actionLink);
  if (ref?.type !== 'CUSTOMER') return null;
  const route = resolveEntityRoute(ref);
  if (!route) return null;
  return { route };
}

interface UseWebSocketOptions {
  /**
   * Whether to show toast notifications for incoming messages
   * Default: true for urgent priority only
   */
  showToasts?: boolean;
  /**
   * Only show toasts for notifications with these priorities
   * Default: ['urgent']
   */
  toastPriorities?: string[];
}

/**
 * Hook to manage WebSocket connection and handle real-time updates
 */
/**
 * Dispatch job progress. Refreshes subscriptions, the dispatch-job list and
 * every visit list a dispatch adds to (an open customer page included, even
 * when the dispatch came from another tab: RUT-4441), then emits a
 * `dispatch-job-progress` event for progress UI.
 */
export function handleDispatchJobsEvent(
  queryClient: QueryClient,
  payload: BroadcastMessage['payload'],
) {
  queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
  queryClient.invalidateQueries({ queryKey: ['dispatch-jobs'] });
  void invalidateDispatchedVisits(queryClient);
  window.dispatchEvent(new CustomEvent('dispatch-job-progress', { detail: payload }));
}

export function useWebSocket(options: UseWebSocketOptions = {}) {
  const { showToasts = true, toastPriorities = ['urgent'] } = options;

  const queryClient = useQueryClient();
  const { toast } = useToast();
  const navigate = useNavigate();
  const wsClientRef = useRef(getWebSocketClient(getIdToken));
  const isConnectedRef = useRef(false);

  // Store options in refs to avoid re-running the effect when they change
  const optionsRef = useRef({ showToasts, toastPriorities, toast, navigate });
  optionsRef.current = { showToasts, toastPriorities, toast, navigate };

  useEffect(() => {
    const wsClient = wsClientRef.current;
    const wsUrl = import.meta.env.VITE_WS_URL;

    if (!wsUrl) {
      return;
    }

    const token = getIdToken();
    if (!token) {
      return;
    }

    // Skip WebSocket connection during onboarding (user not yet in an organization)
    // The backend requires custom:orgId claim which isn't present until org setup is complete
    try {
      const decoded = jwtDecode<{ 'custom:orgId'?: string }>(token);
      if (!decoded['custom:orgId']) {
        return;
      }
    } catch {
      return;
    }

    // Handler for notifications - uses refs to avoid stale closures
    const handleNotification = (message: BroadcastMessage) => {
      // Invalidate notifications query to trigger refetch
      queryClient.invalidateQueries({ queryKey: ['notifications'] });

      // Invalidate related data based on notification type
      if (message.payload?.notificationType === 'customer_message') {
        queryClient.invalidateQueries({ queryKey: ['messages', 'conversations'] });
        queryClient.invalidateQueries({ queryKey: ['messages', 'list'] });
      }

      if (message.payload?.entityType) {
        const entityType = message.payload.entityType;
        switch (entityType) {
          case 'visit':
            queryClient.invalidateQueries({ queryKey: ['visits'] });
            break;
          case 'customer':
            queryClient.invalidateQueries({ queryKey: ['customers'] });
            break;
          case 'subscription':
          case 'service':
            queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
            break;
          case 'invoice':
            queryClient.invalidateQueries({ queryKey: ['invoices'] });
            break;
          case 'rate_request':
            queryClient.invalidateQueries({ queryKey: ['rate-requests'] });
            break;
        }
      }

      const hiddenTypes =
        queryClient.getQueryData<UserProfileResponse>(queryKeys.users.profile())
          ?.notificationPreferences?.hiddenInAppTypes ?? [];
      const typeHidden = isHiddenInAppType(message.payload?.notificationType, hiddenTypes);

      // Ring the register the moment money lands (RUT-3168). Driven off the
      // notification type rather than the toast, so the cue is independent of
      // however toasts are gated; the sound module itself honours the mute
      // preference and stays silent when the tab is in the background.
      // Types hidden from the bell stay silent too (RUT-3531).
      const playSound = typeHidden ? null : getNotificationSound(message.payload?.notificationType);
      if (playSound) {
        const cueKey =
          message.payload?.notificationId ??
          `${message.payload?.notificationType}:${message.payload?.title}`;
        playAlertCueOnce(cueKey, playSound);
      }

      // Show toast for urgent notifications, plus the alerts that always toast
      const { showToasts, toastPriorities, toast, navigate } = optionsRef.current;
      const alwaysToast = ALWAYS_TOAST_TYPES.has(message.payload?.notificationType ?? '');
      if (
        showToasts &&
        message.payload &&
        !typeHidden &&
        (alwaysToast ||
          (message.payload.priority && toastPriorities.includes(message.payload.priority)))
      ) {
        const reply = getReplyAction(
          message.payload.notificationType,
          message.payload.actionLink,
        );
        const setRate = getSetRateAction(
          message.payload.notificationType,
          message.payload.actionLink,
        );
        const viewCustomer = getViewCustomerAction(
          message.payload.notificationType,
          message.payload.actionLink,
        );
        const moneyAlert = getMoneyAlertAction(
          message.payload.notificationType,
          message.payload.actionLink,
        );
        let action: ToastActionConfig | undefined;
        if (reply) {
          action = {
            label: 'Reply',
            altText: 'Reply',
            onClick: () => {
              setMessageDraft(reply.customerId, '');
              navigate(reply.route);
            },
          };
        } else if (setRate) {
          action = {
            label: 'Set rate',
            altText: 'Set rate',
            onClick: () => navigate(setRate.route),
          };
        } else if (viewCustomer) {
          action = {
            label: 'View customer',
            altText: 'View customer',
            onClick: () => navigate(viewCustomer.route),
          };
        } else if (moneyAlert) {
          action = {
            label: moneyAlert.label,
            altText: moneyAlert.label,
            onClick: () => navigate(moneyAlert.route),
          };
        }
        toast({
          title: message.payload.title || 'New Notification',
          description: message.payload.message || undefined,
          variant: message.payload.priority === 'urgent' ? 'destructive' : 'default',
          action,
        });
      }
    };

    // Handler for data updates
    const handleDataUpdate = (message: BroadcastMessage) => {

      if (message.payload?.entityType) {
        const entityType = message.payload.entityType;
        switch (entityType) {
          case WS_ENTITY_TYPES.LOCATION_TRACKING: {
            // Optimistically update the crew locations cache with the new location data
            // The WebSocket payload includes the full location data for immediate UI update
            // Use type assertion since the payload schema varies by entity type
            const locationPayload = message.payload as {
              memberId?: string;
              memberName?: string;
              crewId?: string;
              crewName?: string;
              latitude?: number;
              longitude?: number;
              timestamp?: string;
              isClocked?: boolean;
            } & typeof message.payload;

            if (locationPayload.memberId && locationPayload.latitude !== undefined) {
              const locationUpdate = {
                memberId: locationPayload.memberId,
                crewMemberId: locationPayload.memberId,
                memberName: locationPayload.memberName,
                crewMemberName: locationPayload.memberName,
                crewId: locationPayload.crewId,
                crewName: locationPayload.crewName,
                latitude: locationPayload.latitude,
                longitude: locationPayload.longitude,
                coordinates: [locationPayload.longitude, locationPayload.latitude] as [number, number],
                timestamp: locationPayload.timestamp,
                lastUpdated: locationPayload.timestamp,
                isClocked: locationPayload.isClocked,
              };

              // Optimistically update the crew locations cache
              queryClient.setQueryData(['crews', 'locations', undefined], (oldData: any) => {
                if (!oldData) {
                  return locationPayload.isClocked
                    ? { locations: [locationUpdate], activeVisits: [], lastUpdated: new Date().toISOString() }
                    : undefined;
                }

                if (!oldData.locations) {
                  return locationPayload.isClocked
                    ? { ...oldData, locations: [locationUpdate], lastUpdated: new Date().toISOString() }
                    : oldData;
                }

                const existingIndex = oldData.locations.findIndex(
                  (loc: any) => loc.memberId === locationUpdate.memberId || loc.crewMemberId === locationUpdate.memberId
                );

                const newLocations = [...oldData.locations];
                if (existingIndex >= 0) {
                  newLocations[existingIndex] = { ...newLocations[existingIndex], ...locationUpdate };
                } else if (locationPayload.isClocked) {
                  newLocations.push(locationUpdate);
                }

                return {
                  ...oldData,
                  locations: newLocations,
                  lastUpdated: new Date().toISOString(),
                };
              });
            }
            break;
          }
          case WS_ENTITY_TYPES.VISITS:
            queryClient.invalidateQueries({ queryKey: ['visits'] });
            queryClient.invalidateQueries({ queryKey: ['dashboard', 'stats'] });
            break;
          case WS_ENTITY_TYPES.MESSAGE_CAMPAIGNS:
            // Invalidate message campaigns for real-time status updates
            queryClient.invalidateQueries({ queryKey: ['message-campaigns'] });
            break;
          case WS_ENTITY_TYPES.CONVERSATIONS:
            queryClient.invalidateQueries({ queryKey: ['messages', 'conversations'] });
            // Also invalidate individual message threads in case the active one got a new message
            queryClient.invalidateQueries({ queryKey: ['messages', 'list'] });
            break;
          case WS_ENTITY_TYPES.DISPATCH_JOBS:
            handleDispatchJobsEvent(queryClient, message.payload);
            break;
          default:
            // Generic fallback for other entity types
            queryClient.invalidateQueries({ queryKey: [entityType] });
        }
      }
    };

    // Handler for chat messages
    const handleChatMessage = (message: BroadcastMessage) => {
      console.log('[useWebSocket] Received chat_message');
      // Invalidate chat queries so conversation list and active thread refresh
      queryClient.invalidateQueries({ queryKey: ['chat', 'conversations'] });
      queryClient.invalidateQueries({ queryKey: ['chat', 'unread-count'] });
      // Invalidate the specific conversation's messages if we have the conversationId
      const conversationId = (message.payload?.data as any)?.conversationId;
      if (conversationId) {
        queryClient.invalidateQueries({ queryKey: ['chat', 'messages', conversationId] });
      }
    };

    // Subscribe to events
    const unsubscribeNotification = wsClient.subscribe('notification', handleNotification);
    const unsubscribeDataUpdate = wsClient.subscribe('data_update', handleDataUpdate);
    const unsubscribeChatMessage = wsClient.subscribe('chat_message', handleChatMessage);
    const unsubscribeConnected = wsClient.subscribe('_connected', () => {
      isConnectedRef.current = true;
    });
    const unsubscribeDisconnected = wsClient.subscribe('_disconnected', () => {
      isConnectedRef.current = false;
    });

    // Delay connection slightly to avoid React StrictMode's rapid mount/unmount cycle
    let cancelled = false;
    const connectTimeout = setTimeout(() => {
      if (!cancelled) {
        wsClient.connect();
      }
    }, 100);

    // Cleanup on unmount
    return () => {
      cancelled = true;
      clearTimeout(connectTimeout);
      unsubscribeNotification();
      unsubscribeDataUpdate();
      unsubscribeChatMessage();
      unsubscribeConnected();
      unsubscribeDisconnected();
      wsClient.disconnect();
    };
    // queryClient is stable, so this effect only runs once on mount
  }, [queryClient]);

  // Reconnect when token changes (e.g., after refresh)
  useEffect(() => {
    const handleTokenUpdate = () => {
      const wsClient = wsClientRef.current;
      const wsUrl = import.meta.env.VITE_WS_URL;

      if (!wsUrl) return;

      const token = getIdToken();
      if (token && wsClient.getState() === 'connected') {
        console.log('[useWebSocket] Token updated, reconnecting');
        wsClient.reconnect();
      }
    };

    window.addEventListener('auth:tokens_updated', handleTokenUpdate);
    return () => window.removeEventListener('auth:tokens_updated', handleTokenUpdate);
  }, []);

  return {
    /**
     * Get current connection state
     */
    isConnected: () => wsClientRef.current.getState() === 'connected',

    /**
     * Get the WebSocket client state
     */
    getState: () => wsClientRef.current.getState(),

    /**
     * Manually trigger a reconnect
     */
    reconnect: () => wsClientRef.current.reconnect(),
  };
}

/**
 * Reset the WebSocket client singleton
 * Call this on logout to clean up the connection
 */
export { resetWebSocketClient };
